import { notifications } from '@mantine/notifications';
import { DeviceListAllEvidenceFieldsResponse } from 'api/actions/device-list-all-evidence-fields/device-list-all-evidence-fields-response';
import { useApi } from 'api/api-context';
import EvidenceFieldForm, { EvidenceFieldFormData } from 'components/forms/evidence-field/EvidenceFieldForm';
import { useConfirm } from 'components/modals/message/MessageProvider';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, EVIDENCE_FIELDS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

type EvidenceField = DeviceListAllEvidenceFieldsResponse[number];

/**
 * Page used to edit the evidence field.
 */
export default function EditEvidenceFieldPage() {
  const navigate = useNavigate();
  const { evidenceFieldId } = useParams();
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const [data, setData] = useState<EvidenceField | null>(null);
  const { roleId } = useApi();

  const initialValues = useMemo(() => {
    if (!data) {
      return null;
    }

    return {
      ...data,
      sectionId: String(data.sectionId),
      selectOptions: data.selectOptions ?? [],
      predefinedDataTypeId: data.predefinedDataTypeId ? String(data.predefinedDataTypeId) : undefined,
      selectedDeviceTypes: data.deviceTypes?.map(({ deviceTypeId }) => String(deviceTypeId)) ?? [],
      selectedOrganizations: data.organizations?.map(({ organizationId }) => String(organizationId)) ?? [],
    } satisfies EvidenceFieldFormData;
  }, [data]);

  /**
   * Runs when the form is submitted.
   */
  function onSubmit({
    selectedDeviceTypes,
    selectedOrganizations,
    precision = 0,
    predefinedDataTypeId,
    selectOptions = [],
    ...data
  }: EvidenceFieldFormData) {
    const updateField = getAction('DeviceUpdateEvidenceField');
    const removesDeviceValues = Boolean(initialValues?.appliesToDevices && !data.appliesToDevices);
    const removesPersonValues = Boolean(initialValues?.appliesToPeople && !data.appliesToPeople);
    const removalDescription =
      removesDeviceValues && removesPersonValues
        ? 'zo zariadení a od osôb'
        : removesDeviceValues
        ? 'zo zariadení'
        : 'od osôb';

    const payload = {
      ...data,
      sectionId: Number(data.sectionId),
      precision,
      selectOptions: selectOptions.map((option) => option.trim()),
      predefinedDataTypeId: predefinedDataTypeId ? Number(predefinedDataTypeId) : undefined,
      allDeviceTypes: data.appliesToDevices && data.allDeviceTypes,
      ...(removesDeviceValues || removesPersonValues ? { confirmApplicabilityValueRemoval: true } : {}),
      selectedDeviceTypes: data.appliesToDevices ? selectedDeviceTypes.map(Number) : [],
      selectedOrganizations: selectedOrganizations.map(Number),
    };

    /** Persists the confirmed applicability and field changes. */
    const update = () =>
      updateField({ payload, parameters: { evidenceFieldId: String(evidenceFieldId) } })
        .then(() => {
          notifications.show({
            title: 'Evidenčné pole bolo upravené',
            message: `Evidenčné pole ${data.fieldName} bol úspešne upravený.`,
            color: SUCCESS_NOTIFICATION_COLOR,
          });

          navigate(EVIDENCE_FIELDS_PAGE_PATH.original);
        })
        .catch(panic);

    if (removesDeviceValues || removesPersonValues) {
      confirm({
        title: 'Odstrániť evidenčné hodnoty',
        content: `Zmena použitia odstráni existujúce hodnoty ${removalDescription}.`,
        primaryButtonText: 'Pokračovať',
        secondaryButtonText: 'Zrušiť',
        onConfirm: update,
      });
      return Promise.resolve();
    }

    return update();
  }

  useEffect(() => {
    const getFields = getAction('DeviceListAllEvidenceFields');

    getFields({ query: { filters: { 'evidenceFieldId.eq': String(evidenceFieldId) } } })
      .then(([evidenceField]) => setData(evidenceField))
      .catch(panic);
  }, []);

  if (roleId !== ROLE_ADMIN_ID) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  }

  return (
    <DashboardLayout
      title={`Úprava evidenčného poľa: ${data?.fieldName ?? ''}`}
      breadcrumbs={[
        { title: 'Evidenčné polia', link: EVIDENCE_FIELDS_PAGE_PATH.original },
        { title: data?.fieldName ?? 'Načítavanie ...' },
      ]}
    >
      {initialValues && <EvidenceFieldForm initialValues={initialValues} onSubmit={onSubmit} />}
    </DashboardLayout>
  );
}
